import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import type { ImpactMenuCoordinatorContextValue } from "@/interfaces/impactReport";

/* ─────────────────────────────────────────────────────────────────────────────────────────────
   THE SINGLE-OPEN COORDINATOR for the impact report's control bar.

   Mounted around the whole bar (`ImpactControlBar.tsx`) so it is an ancestor of every
   `ImpactFilterMenu` instance on it — the four controls (VIEW, CAUSE, CAMPAIGN, DATE) plus the
   collapsed active-filters pill in `ImpactFilterPills.tsx`, which also renders one.

   WHY A SEPARATE CONTEXT rather than folding this into `ImpactReportContext`: that context holds
   the report's DATA state (controls, filter, cause options) and is mounted app-wide around
   `<Drawer />`. This holds transient UI state — which popover is open right now — scoped to
   exactly the one section of the app bar that has more than one popover. Mixing the two would
   re-render every `ImpactReportContext` consumer (including the page body) on every menu
   open/close, for a value none of them read.

   OPTIONAL BY DESIGN: `useImpactMenuCoordinator()` returns `null` outside a provider rather than
   throwing. `ImpactFilterMenu` is a general dropdown primitive, not something that only exists
   inside a coordinated bar — falling back to fully independent `anchorEl` state (today's
   behaviour) when there is no coordinator keeps it usable anywhere else it might ever be mounted.
   ───────────────────────────────────────────────────────────────────────────────────────────── */

const ImpactMenuCoordinatorContext = createContext<ImpactMenuCoordinatorContextValue | null>(null);

export function ImpactMenuCoordinatorProvider({ children }: { children: ReactNode }) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const openMenu = useCallback((id: string) => setOpenMenuId(id), []);

  // Guarded rather than an unconditional `setOpenMenuId(null)` — see
  // `ImpactMenuCoordinatorContextValue.closeMenu`'s own comment for the race this prevents.
  const closeMenu = useCallback((id: string) => {
    setOpenMenuId((current) => (current === id ? null : current));
  }, []);

  const value = useMemo<ImpactMenuCoordinatorContextValue>(
    () => ({ openMenuId, openMenu, closeMenu }),
    [openMenuId, openMenu, closeMenu],
  );

  return (
    <ImpactMenuCoordinatorContext.Provider value={value}>
      {children}
    </ImpactMenuCoordinatorContext.Provider>
  );
}

/**
 * The shared "which menu is open" slot, or `null` outside a provider — see the file comment on why
 * `null` is a valid, supported answer here rather than a thrown error.
 */
export function useImpactMenuCoordinator(): ImpactMenuCoordinatorContextValue | null {
  return useContext(ImpactMenuCoordinatorContext);
}
